import React, { useState, useEffect } from 'react';
import { useGame } from '../context/GameContext.jsx';
import logoWhite from '../../assets/images/logo-white.png';

const InitialScreen = () => {
  const {
    createSession,
    joinSession,
    translations,
    lang,
    setLang,
    setError,
    isLoading,
    validateCodeAndFetchLanguages,
    clearHypothesisLocalStorage,
    cardLang,
    checkSession
  } = useGame();

  const [showJoinInput, setShowJoinInput] = useState(false);
  const [sessionIdInput, setSessionIdInput] = useState('');
  const [scannedCode, setScannedCode] = useState(null);
  const [isCodeValidated, setIsCodeValidated] = useState(false);
  const [isScannedCodeValid, setIsScannedCodeValid] = useState(false);

  const [isStoredSessionCheckedAndValid, setIsStoredSessionCheckedAndValid] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('c');

    const validateAndSetCode = async (cCode) => {
      if (cCode) {
        setScannedCode(cCode);
        setIsCodeValidated(true);
        const success = await validateCodeAndFetchLanguages(cCode);
        setIsScannedCodeValid(success);
        if (!success) {
          setError(translations[lang]?.error_code_not_found);
        }
      } else {
        setScannedCode(null);
        setIsCodeValidated(true);
        setIsScannedCodeValid(false);
      }
    }
    validateAndSetCode(code);
  }, [lang, validateCodeAndFetchLanguages, setError, translations]);

  useEffect(() => {
    const verifyStoredSession = async () => {
      const storedSessionId = localStorage.getItem('sessionId');
      if (storedSessionId) {
        const isValid = await checkSession(storedSessionId);
        setIsStoredSessionCheckedAndValid(isValid);
        if (!isValid) {
          localStorage.removeItem('sessionId');
          clearHypothesisLocalStorage();
        }
      }
    };
    verifyStoredSession();
  }, [checkSession, clearHypothesisLocalStorage]);

  useEffect(() => {
    if (!showJoinInput) {
      setSessionIdInput('');
    }
  }, [showJoinInput]);

  const handleJoinSession = () => {
    const trimmedId = sessionIdInput.trim().toUpperCase();
    if (trimmedId.length === 6) {
      joinSession(trimmedId, lang, cardLang);
    } else {
      setError(translations[lang]?.error_session_invalid_length);
    }
  };

  const handleLanguageChange = (newLang) => {
    if (translations[newLang] && newLang !== lang) {
      setLang(newLang);
      localStorage.setItem('language', newLang);
      if (scannedCode) {
        validateCodeAndFetchLanguages(scannedCode);
      }
    }
  };

  const actionButtonsDisabled = isLoading || !isCodeValidated || !isScannedCodeValid;

  const storedSessionId = localStorage.getItem('sessionId');

  return (
    <div className="screen-visible">
      <div style={{position: "absolute", bottom: "10px", right: "10px", fontSize: "0.7rem", color: "#aaa"}}>version 2025.01</div>
      <div style={{position: "absolute", bottom: "10px", left: "10px", fontSize: "0.7rem", color: "#aaa"}}>&copy; 2025 Pixelings Studio. All rights reserved.</div>

      <img src={logoWhite} alt={translations[lang]?.game_logo_alt} className="game-logo mb-4" />
      <p className="mb-4">
        {scannedCode
          ? (isScannedCodeValid
            ? (translations[lang]?.scanned_code_ready?.replace('{0}', scannedCode))
            : (translations[lang]?.error_code_not_found))
          : (translations[lang]?.initial_screen_subtitle)
        }
      </p>

      <div className="d-grid gap-2 mb-3">
        <button
          className="btn btn-primary btn-lg"
          onClick={() => createSession(scannedCode, lang, cardLang)}
          disabled={actionButtonsDisabled}
        >
          {translations[lang]?.create_session_button}
        </button>
        {!showJoinInput ? (
          <button
            className="btn btn-secondary btn-lg"
            onClick={() => setShowJoinInput(true)}
            disabled={actionButtonsDisabled}
          >
            {translations[lang]?.join_session_button}
          </button>
        ) : (
          <div className="input-group mb-3">
            <span className="input-group-text session-code-prefix">#</span>
            <input
              type="text"
              className="form-control"
              placeholder={translations[lang]?.session_code_placeholder}
              maxLength="6"
              value={sessionIdInput}
              onChange={(e) => setSessionIdInput(e.target.value)}
              disabled={actionButtonsDisabled}
            />
            <button
              className="btn btn-success"
              type="button"
              onClick={handleJoinSession}
              disabled={actionButtonsDisabled}
            >
              {translations[lang]?.enter_button}
            </button>
          </div>
        )}
      </div>

      { isStoredSessionCheckedAndValid && (
        <div className="d-grid gap-2 mb-3">
          <button
            className="btn btn-info btn-lg"
            onClick={() => joinSession(storedSessionId, lang, cardLang)}
            disabled={actionButtonsDisabled}
          >
            {translations[lang]?.return_session_button}: {storedSessionId}
          </button>
        </div>
      )}

      <div className="mb-3">
        <p>{translations[lang]?.language_label}</p>
        <div className="d-flex justify-content-center gap-3">
          <img
            className={`flag-icon ${lang === 'BR' ? 'active' : ''}`}
            data-lang="BR"
            src="../assets/images/BR-64.png"
            alt={translations[lang]?.brazil_flag_alt}
            title={translations[lang]?.BR_flag_title}
            onClick={() => handleLanguageChange('BR')}
            style={{ pointerEvents: isLoading ? 'none' : 'auto' }}
          />
          <img
            className={`flag-icon ${lang === 'EN' ? 'active' : ''}`}
            data-lang="EN"
            src="../assets/images/GB-64.png"
            alt={translations[lang]?.uk_flag_alt}
            title={translations[lang]?.EN_flag_title}
            onClick={() => handleLanguageChange('EN')}
            style={{ pointerEvents: isLoading ? 'none' : 'auto' }}
          />
          <img
            className={`flag-icon ${lang === 'PL' ? 'active' : ''}`}
            data-lang="PL"
            src="../assets/images/PL-64.png"
            alt={translations[lang]?.poland_flag_alt}
            title={translations[lang]?.PL_flag_title}
            onClick={() => handleLanguageChange('PL')}
            style={{ pointerEvents: isLoading ? 'none' : 'auto' }}
          />
        </div>
      </div>
    </div>
  );
};

export default InitialScreen;
